import { useCallback, useMemo, useState } from 'react';

import { CFG_MAX_STEPS, PatternKind, PATTERN_KIND_NAMES } from '../../protocol/constants.ts';
import { BAND_TEXT, formatPxPerSec, computeDisruption } from '../../domain/disruption.ts';
import { emptyStep, hasButtonActions, validateDraft, type Draft } from '../../domain/draft.ts';

interface Props {
  draft: Draft;
  onChange: (next: Draft) => void;
  /** Shows resolved problems once true, so a form is not red before it is touched. */
  showErrors: boolean;
  compact?: boolean;
}

const MAX_DELTA = 127;

export function StepTable({ draft, onChange, showErrors, compact = false }: Props) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const disruption = useMemo(() => computeDisruption(draft), [draft]);
  const problems = useMemo(() => validateDraft(draft), [draft]);

  const setSteps = useCallback(
    (steps: Draft['steps']) => onChange({ ...draft, steps }),
    [draft, onChange],
  );

  const patch = useCallback(
    (index: number, field: keyof Draft['steps'][number], value: number) => {
      const next = draft.steps.map((s, i) => (i === index ? { ...s, [field]: value } : s));
      setSteps(next);
    },
    [draft.steps, setSteps],
  );

  const move = useCallback(
    (from: number, to: number) => {
      if (to < 0 || to >= draft.steps.length || from === to) return;
      const next = [...draft.steps];
      const [item] = next.splice(from, 1);
      if (item) next.splice(to, 0, item);
      setSteps(next);
    },
    [draft.steps, setSteps],
  );

  const atLimit = draft.steps.length >= CFG_MAX_STEPS;

  return (
    <section className="panel">
      <div className="row row--between">
        <h2>{compact ? 'Steps' : `Steps (${draft.steps.length})`}</h2>
        {compact && <span className="dim">{draft.steps.length} / {CFG_MAX_STEPS}</span>}
        <div className="row">
          <button
            type="button"
            className="btn btn--small"
            disabled={atLimit}
            onClick={() => setSteps([...draft.steps, emptyStep()])}
          >
            {compact ? 'Add step' : '+ Add step'}
          </button>
          <button
            type="button"
            className="btn btn--small"
            disabled={atLimit || draft.steps.length === 0}
            onClick={() => {
              const last = draft.steps[draft.steps.length - 1];
              if (last) setSteps([...draft.steps, { ...last }]);
            }}
          >
            Duplicate last
          </button>
        </div>
      </div>

      {draft.kind !== PatternKind.MACRO && (
        <p className="dim">
          {compact ? 'Steps are kept, but not used by this pattern.' : <>
            The pattern kind is <strong>{PATTERN_KIND_NAMES[draft.kind]}</strong>, which
            generates its movement from the parameters above. This step list is kept so
            you can switch back to MACRO without losing it, but the device ignores it.
          </>}
        </p>
      )}

      {atLimit && (
        <p className="warn">
          {compact ? `${CFG_MAX_STEPS}-step limit. Remove a step to add another.` : <>
            At the device limit of {CFG_MAX_STEPS} steps. A longer pattern needs to be a
            generator, or split across two saved profiles.
          </>}
        </p>
      )}

      {problems.length > 0 && showErrors && (
        <ul className="problems">
          {problems.map((p, i) => (
            <li key={`${p.field}-${i}`}>{p.message}</li>
          ))}
        </ul>
      )}

      <div className="table-wrap">
        <table className="steps">
          <thead>
            <tr>
              <th className="steps__handle" aria-label="Reorder" />
              <th>#</th>
              <th>dx</th>
              <th>dy</th>
              <th title="Scroll wheel notches">wheel</th>
              <th title="bit0 left, bit1 right, bit2 middle">buttons</th>
              <th title="Delay after emitting this step">delay (ms)</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {draft.steps.map((step, index) => (
              <tr
                key={index}
                className={overIndex === index ? 'steps__row--over' : undefined}
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverIndex(index);
                }}
                onDragLeave={() => setOverIndex((v) => (v === index ? null : v))}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) move(dragIndex, index);
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                }}
              >
                <td className="steps__handle" title="Drag to reorder">
                  ⠿
                </td>
                <td className="steps__index">{index + 1}</td>
                <DeltaCell
                  value={step.dx}
                  onChange={(v) => patch(index, 'dx', v)}
                  label={`step ${index + 1} dx`}
                />
                <DeltaCell
                  value={step.dy}
                  onChange={(v) => patch(index, 'dy', v)}
                  label={`step ${index + 1} dy`}
                />
                <DeltaCell
                  value={step.wheel}
                  onChange={(v) => patch(index, 'wheel', v)}
                  label={`step ${index + 1} wheel`}
                />
                <td>
                  <input
                    className="num"
                    type="number"
                    min={0}
                    max={7}
                    value={step.buttons}
                    aria-label={`step ${index + 1} buttons`}
                    onChange={(e) => patch(index, 'buttons', clampInt(e.target.value, 0, 7))}
                  />
                </td>
                <td>
                  <input
                    className="num"
                    type="number"
                    min={0}
                    max={65535}
                    value={step.delayMs}
                    aria-label={`step ${index + 1} delay in milliseconds`}
                    onChange={(e) => patch(index, 'delayMs', clampInt(e.target.value, 0, 65535))}
                  />
                </td>
                <td className="steps__actions">
                  <button
                    type="button"
                    className="icon"
                    title="Move up"
                    aria-label={`move step ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="icon"
                    title="Move down"
                    aria-label={`move step ${index + 1} down`}
                    disabled={index === draft.steps.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="icon icon--danger"
                    title="Delete"
                    aria-label={`delete step ${index + 1}`}
                    onClick={() => setSteps(draft.steps.filter((_, i) => i !== index))}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {draft.steps.length === 0 && (
              <tr>
                <td colSpan={8} className="dim">
                  {compact ? 'No steps. Add one to begin.' : <>
                    No steps. A MACRO with no steps emits nothing — add one, or pick a
                    generator kind.
                  </>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="dim">
        <strong>{formatPxPerSec(disruption.pxPerSec)}</strong>
        {disruption.estimated ? ' (estimated)' : ''} — {hasButtonActions(draft)
          ? compact ? 'Includes clicks or drags.' : 'Contains clicks or drags; movement speed does not measure button actions.'
          : BAND_TEXT[disruption.band]}
      </p>
    </section>
  );
}

interface DeltaCellProps {
  value: number;
  onChange: (value: number) => void;
  label: string;
}

function DeltaCell({ value, onChange, label }: DeltaCellProps) {
  // `type="number"` with a RangeEvent-free approach: the clamp keeps the value
  // inside what the HID report can carry, so the device never has to NACK it.
  return (
    <td>
      <input
        className={`num ${Math.abs(value) > MAX_DELTA ? 'num--bad' : ''}`}
        type="number"
        min={-MAX_DELTA}
        max={MAX_DELTA}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(clampInt(e.target.value, -MAX_DELTA, MAX_DELTA))}
      />
    </td>
  );
}

/** An empty input reads as 0 rather than NaN, so clearing a cell is not a crash. */
function clampInt(raw: string, min: number, max: number): number {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return 0;
  return Math.min(max, Math.max(min, n));
}
